import { Badge, Button, Card, CardBody } from '@foundation/ui'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './sequences.index.module.css'
import { Skeleton } from '@/components/Skeleton'
import { api, type Sequence } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/sequences/')({
  component: SequencesIndexPage,
})

function SequencesIndexPage() {
  const [sequences, setSequences] = useState<Sequence[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    api
      .listSequences()
      .then((res) => setSequences(res.data))
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to load sequences')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleToggleActive = (sequence: Sequence) => {
    const action = sequence.isActive
      ? api.deactivateSequence(sequence.id)
      : api.activateSequence(sequence.id)

    action
      .then((res) => {
        setSequences((prev) => prev.map((s) => (s.id === sequence.id ? res.data : s)))
        toast.success(sequence.isActive ? 'Sequence paused' : 'Sequence activated')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to update sequence')
      })
  }

  const handleDuplicate = (sequence: Sequence) => {
    api
      .createSequence({
        name: `${sequence.name} (Copy)`,
        ...(sequence.description ? { description: sequence.description } : {}),
      })
      .then((res) => {
        setSequences((prev) => [...prev, res.data])
        toast.success('Sequence duplicated')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to duplicate sequence')
      })
  }

  const handleDelete = (sequence: Sequence) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${sequence.name}"? This action cannot be undone.`
      )
    ) {
      return
    }

    api
      .deleteSequence(sequence.id)
      .then(() => {
        setSequences((prev) => prev.filter((s) => s.id !== sequence.id))
        toast.success('Sequence deleted')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to delete sequence')
      })
  }

  const getStatusBadge = (sequence: Sequence) => {
    if (sequence.isActive) {
      return <Badge variant="success">Active</Badge>
    }
    if (sequence.steps.length === 0) {
      return <Badge variant="default">Draft</Badge>
    }
    return <Badge variant="warning">Paused</Badge>
  }

  const getStatusLabel = (sequence: Sequence): 'active' | 'paused' | 'draft' => {
    if (sequence.isActive) return 'active'
    if (sequence.steps.length === 0) return 'draft'
    return 'paused'
  }

  if (isLoading) {
    return (
      <>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Your win-back sequences</span>
          <Button variant="primary" disabled>
            Create Sequence
          </Button>
        </div>
        <div className={styles.loadingGrid}>
          {[1, 2, 3].map((i) => (
            <Card key={i} className={styles.loadingCard}>
              <CardBody>
                <Skeleton width="60%" height="1.25rem" />
                <Skeleton width="100%" height="0.875rem" />
                <Skeleton variant="rectangular" height="60px" />
                <Skeleton width="40%" height="0.875rem" />
              </CardBody>
            </Card>
          ))}
        </div>
      </>
    )
  }

  if (sequences.length === 0) {
    return (
      <>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Your win-back sequences</span>
        </div>
        <Card className={styles.emptyState ?? ''}>
          <CardBody>
            <div className={styles.emptyIllustration ?? ''}>
              <svg
                width="120"
                height="100"
                viewBox="0 0 120 100"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="10"
                  y="20"
                  width="100"
                  height="65"
                  rx="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.2"
                />
                <path d="M10 28L60 58L110 28" stroke="currentColor" strokeWidth="2" opacity="0.3" />
                <rect
                  x="25"
                  y="5"
                  width="70"
                  height="50"
                  rx="4"
                  stroke="currentColor"
                  strokeWidth="2"
                  opacity="0.15"
                />
                <path d="M25 13L60 38L95 13" stroke="currentColor" strokeWidth="2" opacity="0.2" />
                <circle cx="95" cy="15" r="12" fill="var(--color-primary)" opacity="0.15" />
                <path
                  d="M91 15L94 18L100 12"
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h3 className={styles.emptyTitle ?? ''}>Create your first win-back sequence</h3>
            <p className={styles.emptyDescription ?? ''}>
              Win-back sequences are automated email campaigns that re-engage cancelled subscribers.
              Set up multi-step email flows with personalized offers and track your recovery rate.
            </p>
            <div className={styles.emptyFeatures ?? ''}>
              <div className={styles.emptyFeature ?? ''}>
                <span className={styles.emptyFeatureIcon ?? ''}>1</span>
                <span>Configure email steps with timing and templates</span>
              </div>
              <div className={styles.emptyFeature ?? ''}>
                <span className={styles.emptyFeatureIcon ?? ''}>2</span>
                <span>Add personalized discount offers</span>
              </div>
              <div className={styles.emptyFeature ?? ''}>
                <span className={styles.emptyFeatureIcon ?? ''}>3</span>
                <span>Track recovered revenue automatically</span>
              </div>
            </div>
            <Link to="/sequences/new">
              <Button variant="primary">Create Your First Sequence</Button>
            </Link>
          </CardBody>
        </Card>
      </>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          {sequences.length} sequence{sequences.length !== 1 ? 's' : ''}
        </span>
        <Link to="/sequences/new">
          <Button variant="primary">Create Sequence</Button>
        </Link>
      </div>

      <div className={styles.grid}>
        {sequences.map((sequence) => {
          const status = getStatusLabel(sequence)
          return (
            <Card key={sequence.id} className={styles.sequenceCard}>
              <CardBody>
                <div className={styles.cardHeader}>
                  <div>
                    <h3 className={styles.cardTitle}>
                      <Link
                        to="/sequences/$id"
                        params={{ id: sequence.id }}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                      >
                        {sequence.name}
                      </Link>
                      {sequence.isDefault && <span className={styles.defaultBadge}>Default</span>}
                    </h3>
                  </div>
                  {getStatusBadge(sequence)}
                </div>

                {sequence.description && (
                  <p className={styles.cardDescription}>{sequence.description}</p>
                )}

                <div className={styles.statsRow}>
                  <div className={styles.stat}>
                    <span className={styles.statValue}>{sequence.steps.length}</span>
                    <span className={styles.statLabel}>Steps</span>
                  </div>
                  <div className={styles.stat}>
                    <span className={styles.statValue}>
                      {status === 'active' ? 'Live' : status === 'paused' ? 'Off' : '--'}
                    </span>
                    <span className={styles.statLabel}>Status</span>
                  </div>
                </div>

                <div className={styles.cardFooter}>
                  <span className={styles.cardMeta}>
                    Created {new Date(sequence.createdAt).toLocaleDateString()}
                  </span>
                  <div className={styles.cardActions}>
                    <Button variant="ghost" size="sm" onClick={() => handleToggleActive(sequence)}>
                      {sequence.isActive ? 'Pause' : 'Activate'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(sequence)}>
                      Duplicate
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(sequence)}>
                      Delete
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          )
        })}
      </div>
    </>
  )
}
